import { CHECKIN_PASS_REFUSAL, checkinPassRefusal } from "../../../common/checkinPassRefusal";
import { guardianDenialFromError, isApiError } from "@bw/shared/api";

/**
 * "Redeem one" (#2691): pure rules for the player's check-in code.
 *
 * The backend mints a 40 s pass for an open 10-minute intent; the app asks for
 * a fresh pass every 30 s while the code is on screen, so a screenshot is
 * useless. Nothing is used until a kiosk scans the code.
 */

/** How often a visible code is replaced (the pass itself lives 40 s). */
export const REDEMPTION_REFRESH_MS = 30_000;
/** Treat a pass as unusable a little before its 40 s server lifetime ends. */
export const REDEMPTION_PASS_USABLE_MS = 38_000;
/** A failed refresh (network) is retried this soon while the code still works. */
export const REDEMPTION_RETRY_MS = 5_000;
/** While a code is visible, look for the scan's check-in this often. */
export const REDEMPTION_CHECKED_IN_POLL_MS = 5_000;
/** The intent's own lifetime on the server (REDEMPTION_INTENT_TTL). */
export const REDEMPTION_INTENT_TTL_MS = 10 * 60_000;

export type RedemptionErrorKind =
  | "checked_in"
  | "not_redeemable"
  | "payment"
  | "in_progress"
  | "ended"
  | "guardian"
  | "offline"
  | "unknown";

export interface RedemptionErrorView {
  kind: RedemptionErrorKind;
  title: string;
  message: string;
  /** Whether "Try again" can help (vs. "this pass can't be used now"). */
  retryable: boolean;
}

/**
 * Map a create/refresh failure to the words the player sees. Guardian-control
 * 403s are rendered by the shared guardian notice; this only labels them.
 */
export function redemptionErrorView(error: unknown): RedemptionErrorView {
  if (guardianDenialFromError(error)) {
    return {
      kind: "guardian",
      title: "This needs a guardian first",
      message: "A parent or guardian needs to allow check-in for your account.",
      retryable: false,
    };
  }
  if (isApiError(error)) {
    // Owner rule (#2691): a used code, or a visit already checked in today,
    // never makes a new QR. Not retryable: a later visit can redeem again.
    const refusal = checkinPassRefusal(error);
    if (refusal === "checked_in_today" || refusal === "redemption_used") {
      return {
        kind: "checked_in",
        title: CHECKIN_PASS_REFUSAL[refusal].label,
        message: CHECKIN_PASS_REFUSAL[refusal].message,
        retryable: false,
      };
    }
    if (error.code === "not_redeemable") {
      return {
        kind: "not_redeemable",
        title: "This pass can't be used right now",
        message:
          "It may have expired, run out of visits, or drop-in isn't open here now. Nothing was used.",
        retryable: false,
      };
    }
    if (error.code === "payment_past_due") {
      // Same refusal a visit deduction makes for a past-due pass.
      return {
        kind: "payment",
        title: "Payment problem",
        message: "Update your card in Memberships, then try again. Nothing was used.",
        retryable: false,
      };
    }
    if (error.code === "redemption_in_progress") {
      return {
        kind: "in_progress",
        title: "A check-in is going through",
        message:
          "Your last code is being scanned right now. Wait a moment, then try again.",
        retryable: true,
      };
    }
    if (error.code === "redemption_not_found" || error.status === 404) {
      return {
        kind: "ended",
        title: "This code has ended",
        message: "Get a new code to check in. Nothing was used.",
        retryable: true,
      };
    }
    return {
      kind: "unknown",
      title: "We couldn't make a code",
      message: "Something went wrong on our side. Nothing was used. Try again.",
      retryable: true,
    };
  }
  // fetch() rejects with a TypeError when the network is unreachable.
  return {
    kind: "offline",
    title: "You're offline",
    message: "Connect to the internet to get a check-in code.",
    retryable: true,
  };
}

/**
 * Why a code stopped showing: it timed out unscanned (`expired`), a scan
 * refused it (`released`), or it ended another way (`ended`). Nothing was used
 * in any of these.
 */
export type RedemptionEndReason = "expired" | "released" | "ended";

/** The words under "This code has ended". */
export function endedMessage(reason: RedemptionEndReason): string {
  switch (reason) {
    case "expired":
      return "Codes last 10 minutes. Nothing was used.";
    case "released":
      return "The scan didn't go through, so nothing was used. Get a new code or see the front desk.";
    case "ended":
      return "It was cancelled or replaced. Nothing was used.";
  }
}

/** Whole seconds until `dueAt` (never negative). */
export function secondsUntil(dueAt: number, now: number): number {
  return Math.max(0, Math.ceil((dueAt - now) / 1000));
}

/** Up to this many visits are drawn as dots; larger cards show text only. */
export const MAX_VISIT_DOTS = 20;

/** The visit dots for a pass: `true` for a visit still left. */
export function visitDots(total: number, remaining: number): boolean[] | null {
  if (!Number.isInteger(total) || total <= 0 || total > MAX_VISIT_DOTS) {
    return null;
  }
  const left = Math.max(0, Math.min(total, remaining));
  return Array.from({ length: total }, (_, index) => index < left);
}

function timeParts(iso: string, timeZone?: string): { label: string; period: string } | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone,
    }).formatToParts(date);
    const period = parts.find((part) => part.type === "dayPeriod")?.value ?? "";
    const label = parts
      .filter((part) => part.type !== "dayPeriod")
      .map((part) => part.value)
      .join("")
      .trim();
    return { label, period };
  } catch {
    return timeZone ? timeParts(iso) : null;
  }
}

/** "7:30 PM" in the facility's timezone (the browser's when unknown). */
export function formatFacilityTime(iso: string, timeZone?: string): string {
  const parts = timeParts(iso, timeZone);
  return parts ? `${parts.label} ${parts.period}`.trim() : "";
}

/** "7:30–8:30 PM", or "11:30 AM–12:30 PM" across the period boundary. */
export function formatFacilityTimeRange(
  startIso: string,
  endIso: string,
  timeZone?: string,
): string {
  const start = timeParts(startIso, timeZone);
  const end = timeParts(endIso, timeZone);
  if (!start || !end) return "";
  return start.period === end.period
    ? `${start.label}–${end.label} ${end.period}`
    : `${start.label} ${start.period}–${end.label} ${end.period}`;
}

function facilityDay(date: Date, timeZone?: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat("en-CA").format(date);
  }
}

/** Whether an instant falls on today's date at the facility. */
export function isFacilityToday(startIso: string, now: number, timeZone?: string): boolean {
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return false;
  return facilityDay(start, timeZone) === facilityDay(new Date(now), timeZone);
}

/** Whether a booking is today at the facility and has not ended yet. */
export function isLaterToday(
  startIso: string,
  endIso: string,
  now: number,
  timeZone?: string,
): boolean {
  const start = new Date(startIso);
  const end = Date.parse(endIso);
  if (Number.isNaN(start.getTime()) || !Number.isFinite(end)) return false;
  return end > now && facilityDay(start, timeZone) === facilityDay(new Date(now), timeZone);
}
